<template>
  <section class="invoice-add-wrapper">
    <b-row class="invoice-add">
      <b-col cols="12" xl="9" md="8">
        <b-card no-body class="invoice-preview-card">
          <b-card-body class="invoice-padding pb-0">
            <div
              v-if="showLogo"
              class="d-flex align-items-center justify-content-center"
            >
              <div class="logo-wrapper">
                <template v-if="company?.logo">
                  <img
                    :src="company?.logo"
                    alt="Company Logo"
                    height="160"
                    class="mb-3"
                  />
                </template>
              </div>
            </div>

            <b-row class="invoice-spacing">
              <!-- To Section -->
              <b-col cols="12" md="4">
                <h1 class="mb-3">INVOICE</h1>
                <b>{{ sellerName }}</b>
                <div v-if="sellerEmail">{{ sellerEmail }}</div>
                <div v-if="sellerContact">{{ sellerContact }}</div>
              </b-col>

              <!-- Date Section -->
              <b-col cols="12" md="4" class="text-md-left">
                <div class="mb-3">
                  <h6 class="invoice-title">Invoice No.</h6>
                  <div class="d-flex">
                    <div>{{ invoiceId }}</div>
                    <feather-icon
                      v-b-tooltip.hover
                      icon="CopyIcon"
                      class="ml-1 cursor-pointer"
                      title="Copy invoice ID"
                      size="18"
                      @click="copyToClipboard(invoiceId)"
                    />
                  </div>
                </div>

                <div class="mb-3">
                  <h6 class="invoice-title">Invoice Date</h6>
                  <div>{{ invoiceDate }}</div>
                </div>

                <!--                <div class="mb-3">-->
                <!--                  <h6 class="invoice-title">Due Date</h6>-->
                <!--                  <div>{{ paymentDate }}</div>-->
                <!--                </div>-->
              </b-col>

              <!-- From Section -->
              <b-col cols="12" md="4" class="text-center text-md-left">
                <h6 class="mb-3">{{ company?.name }}</h6>
                <div v-html="company?.address"></div>
                <p class="card-text mb-0">Phone: {{ company?.contact_no }}</p>
                <p class="card-text mb-0">Email: {{ company?.email }}</p>
              </b-col>
            </b-row>
          </b-card-body>

          <b-card-body class="invoice-padding form-item-section">
            <div ref="form" class="repeater-form">
              <div class="table-responsive">
                <table class="table table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Item</th>
                      <th>Project</th>
                      <th>Type</th>
                      <th class="text-right">Amount ({{ getCurrency() }})</th>
                      <th class="text-right">Discount ({{ getCurrency() }})</th>
                      <th class="text-right">Final Amount ({{ getCurrency() }})</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in invoiceItems" :key="index">
                      <td>
                        <div v-if="item.backlink_url">
                          <span class="font-weight-bold">Backlink: </span>
                          <a
                            :href="getBacklinkUrl(item)"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {{ formatWebsite(getBacklinkUrl(item)) }}
                          </a>
                        </div>
                        <div v-if="item.targeted_url">
                          <span class="font-weight-bold">URL: </span>
                          <a
                            :href="item.targeted_url"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {{ item.targeted_url }}
                          </a>
                        </div>
                      </td>
                      <td>{{ item.project?.data?.title || "N/A" }}</td>
                      <td>{{ item.backlinks_type_text || "N/A" }}</td>
                      <td class="text-right">
                        {{ item.cost_price || "0.00" }}
                      </td>
                      <td class="text-right">
                        {{ formatItemDiscount(item) }}
                      </td>
                      <td class="text-right">
                        {{ formatItemFinalAmount(item) }}
                      </td>
                    </tr>

                    <tr>
                      <td colspan="3" class="text-right border-0">
                        <strong>Total</strong>
                      </td>
                      <td class="text-right">
                        <strong>{{ totalBeforeDiscount }}</strong>
                      </td>
                      <td class="text-right">
                        <strong>{{ totalBacklinkDiscount }}</strong>
                      </td>
                      <td class="text-right">
                        <strong>{{ parseFloat(subTotalAmount || 0).toFixed(2) }}</strong>
                      </td>
                    </tr>
                    <tr v-if="parseFloat(discountAmount || 0) > 0">
                      <td colspan="5" class="text-right border-0">
                        <strong
                          >Additional Discount<template
                            v-if="discountType === 'percentage'"
                          >
                            ({{ discount_value }}%)</template
                          ></strong
                        >
                      </td>
                      <td class="text-right">
                        <strong>{{ parseFloat(discountAmount || 0).toFixed(2) }}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="5" class="text-right border-0">
                        <strong
                          >Additional Fee<template v-if="feeType === 'percentage'">
                            ({{ fee_value }}%)</template
                          ></strong
                        >
                      </td>
                      <td class="text-right">
                        <strong>{{ parseFloat(feeAmount || 0).toFixed(2) }}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="5" class="text-right border-0">
                        <strong>Grand Total</strong>
                      </td>
                      <td class="text-right text-danger">
                        <strong>{{ parseFloat(grandTotalAmount || 0).toFixed(2) }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="mt-5">
                <h4 class="mb-1" style="text-transform: capitalize">
                  In Words: {{ getCurrency() }}
                  {{
                    numWords(
                      grandTotalAmount % 1 === 0
                        ? grandTotalAmount
                        : Math.round(grandTotalAmount)
                    )
                  }}
                  <span v-if="grandTotalAmount % 1 !== 0">approximately</span>
                  Only
                </h4>

                <h5 v-if="remarks" class="mb-1">Terms & Conditions:</h5>
                <b v-if="remarks">{{ remarks ?? "N/A" }}</b>
              </div>
            </div>
          </b-card-body>

          <h1 class="text-center my-5">Thank You For Your Business</h1>

          <br />
        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col cols="12" md="4" xl="3" class="invoice-actions">
        <!-- Action Buttons -->
        <b-card>
          <b-row>
            <b-col cols="6">
              <b-form-checkbox
                id="customSwitch1"
                v-model="showLogo"
                switch
                class="custom-toggle"
              >
                Show Logo
              </b-form-checkbox>
            </b-col>
            <b-col cols="6" class="text-right">
              <template v-if="status === 'paid'">
                <b-badge variant="success">Paid</b-badge>
              </template>
              <template v-else>
                <b-badge variant="danger">Due</b-badge>
              </template>
            </b-col>
          </b-row>

          <b-button
            v-if="$permissionAbility(OUTREACH_INVOICE_PAYMENT, permissions)"
            :disabled="status === paymentPaidConstants"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="success"
            class="mb-75"
            block
            @click="payInvoice()"
          >
            {{ status === paymentPaidConstants ? "Paid" : "Pay" }}
          </b-button>

          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            v-on:click="printInvoice"
          >
            Print
          </b-button>

          <b-button
            v-if="$permissionAbility(OUTREACH_INVOICE_ATTACHMENT_UPLOAD_AFTER_PAYMENT, permissions)"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="primary"
            class="mb-75"
            block
            @click="addAttachment()"
          >
            Add Attachment
          </b-button>

          <!--          <div class="my-3">-->
          <!--            <h6>Reviewer:</h6>-->
          <!--            <UserCard :user="approver" />-->
          <!--          </div>-->
        </b-card>

        <!--        Payment card-->
        <b-card>
          <b-col cols="12" class="mt-xl-0 mt-2">
            <div v-if="paymentMethod">
              <table>
                <tbody>
                  <tr>
                    <td><b>Payment Method:</b></td>
                    <td>{{ paymentMethod }}</td>
                  </tr>
                </tbody>
              </table>

              <div v-if="attachments.length > 0">
                <div><b>Attachments:</b></div>
                <div v-for="(attachment, index) in attachments" :key="attachment.id">
                  <b-link
                    :href="attachment.url"
                    target="_blank"
                    class="d-flex align-items-center"
                  >
                    <feather-icon icon="EyeIcon" class="mr-2" />
                    Attachment - {{ index + 1 }}
                  </b-link>
                </div>
              </div>

              <div v-if="paymentDetails">
                <div>
                  <b>Payment Details:</b>
                </div>
                <br />
                <div v-html="paymentDetails"></div>
              </div>
            </div>
          </b-col>
        </b-card>
        <!-- Activity Logs Section -->
        <b-row class="mt-4">
          <b-col cols="12" class="mt-xl-0 mt-2">
            <b-card class="force-full-width">
              <b>Activity Logs:</b>

              <div class="mt-2" title="Activity Logs">
                <div
                  ref="scrollContainer"
                  class="log-scrollable"
                  @scroll="handleScroll"
                >
                  <app-timeline v-if="activityLogs.length > 0">
                    <template v-for="(log, index) in activityLogs">
                      <app-timeline-item
                        v-if="formatLogTitle(log)"
                        :key="index"
                        variant="primary"
                      >
                        <!-- {{ log }} -->
                        <div
                          class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0"
                        >
                          <span v-html="formatLogTitle(log)"></span>
                          <small class="text-muted">
                            {{ formatLogDateTime(log.created_at) }}
                          </small>
                        </div>
                        <div v-if="shouldShowChangesSection(log)" class="mt-1">
                          <ul class="list-unstyled">
                            <li
                              v-for="(value, key) in getFilteredAttributes(log)"
                              :key="key"
                            >
                              <template
                                v-if="shouldShowAttribute(log, key, value)"
                              >
                                <!-- {{ formatChangeKey(key) == "Payment Date" }} -->
                                <div
                                  v-if="formatChangeKey(key) != 'Payment Date'"
                                >
                                  <small v-if="hasDisplayableChanges(log)"
                                    >Changes:</small
                                  >
                                  <br />
                                  <strong>{{ formatChangeKey(key) }}:</strong>
                                  {{ formatChangeValue(key, value) }}

                                  <span
                                    v-if="
                                      formatChangeKey(key) != 'Payment Date' &&
                                      log.event === 'updated' &&
                                      log.properties.old &&
                                      log.properties.old[key] !== undefined &&
                                      log.properties.old[key] !== value
                                    "
                                    class="text-muted ml-50"
                                  >
                                    (From:
                                    {{
                                      formatChangeValue(
                                        key,
                                        log.properties.old[key]
                                      )
                                    }})
                              
                                  </span>
                                </div>
                              </template>
                            </li>
                          </ul>
                        </div>
                      </app-timeline-item>
                    </template>

                    <div v-if="loadingLog" class="text-center my-2">
                      <b-spinner small label="Loading..." />
                    </div>
                  </app-timeline>

                  <div v-else-if="!loadingLog" class="text-center mt-2">
                    <b>No Activity Found</b>
                  </div>

                  <div v-else class="text-center my-2">
                    <b-spinner small label="Loading..." />
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <!-- Printed Invoice  -->
    <div id="content" style="width: 90%; margin: 0; padding: 0; display: none">
      <div style="width: 90%; max-width: 800px; margin: auto">
        <div v-if="showLogo" style="text-align: center; margin-bottom: 20px">
          <div style="margin-bottom: 20px">
            <img
              v-if="company?.logo"
              :src="company?.logo"
              alt="Company Logo"
              height="160"
            />
          </div>
        </div>
        <div v-else>
          <div style="margin-top: 160px"></div>
        </div>

        <div style="display: flex; justify-content: space-between">
          <div style="width: 37%; overflow-wrap: break-word">
            <h1 style="margin-top: 0px; font-size: 28px">INVOICE</h1>
            <b>{{ sellerName }}</b>
            <div v-if="sellerEmail">{{ sellerEmail }}</div>
            <div v-if="sellerContact">{{ sellerContact }}</div>
          </div>

          <div
            style="text-align: center; width: 26%; overflow-wrap: break-word"
          >
            <div class="margin-bottom:5px">
              <strong>Invoice No.</strong>
              <div style="padding: 5px">#{{ invoiceId }}</div>
            </div>
            <br />
            <div class="margin-bottom:5px">
              <strong>Invoice Date</strong>
              <div style="padding: 5px">{{ invoiceDate }}</div>
            </div>
            <br />
            <!--            <div class="margin-bottom:5px">-->
            <!--              <strong>Due Date</strong>-->
            <!--              <div style="padding: 5px;">{{ paymentDate }}</div>-->
            <!--            </div>-->
          </div>

          <div style="width: 37%; overflow-wrap: break-word">
            <strong>{{ company?.name }}</strong>
            <br />
            <br />
            <div>{{ company?.address }}</div>
            <div>Phone: {{ company?.contact_no }}</div>
            <div>Email: {{ company?.email }}</div>
          </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin-top: 20px">
          <thead>
            <tr>
              <th style="border: 1px solid black; padding: 8px">Item</th>
              <th style="border: 1px solid black; padding: 8px">Project</th>
              <th style="border: 1px solid black; padding: 8px">Type</th>
              <th
                style="
                  border: 1px solid black;
                  padding: 8px;
                  text-align: right;
                  width: 10%;
                "
              >
                Amount ({{ getCurrency() }})
              </th>
              <th
                style="
                  border: 1px solid black;
                  padding: 8px;
                  text-align: right;
                  width: 10%;
                "
              >
                Discount ({{ getCurrency() }})
              </th>
              <th
                style="
                  border: 1px solid black;
                  padding: 8px;
                  text-align: right;
                  width: 12%;
                "
              >
                Final Amount ({{ getCurrency() }})
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in invoiceItems" :key="index">
              <td style="border: 1px solid black; padding: 8px">
                <div v-if="item.backlink_url">
                  <span style="font-weight: bold">Backlink: </span>
                  <a
                    :href="getBacklinkUrl(item)"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ formatWebsite(getBacklinkUrl(item)) }}</a
                  >
                </div>
                <div v-if="item.targeted_url">
                  <span style="font-weight: bold">URL: </span>
                  <a
                    :href="item.targeted_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ item.targeted_url }}</a
                  >
                </div>
              </td>
              <td style="border: 1px solid black; padding: 8px">
                {{ item.project?.data?.title || "N/A" }}
              </td>
              <td style="border: 1px solid black; padding: 8px">
                {{ item.backlinks_type_text || "N/A" }}
              </td>
              <td
                style="
                  border: 1px solid black;
                  padding: 8px;
                  text-align: right;
                  width: 10%;
                "
              >
                {{ item.cost_price || "0.00" }}
              </td>
              <td
                style="
                  border: 1px solid black;
                  padding: 8px;
                  text-align: right;
                  width: 10%;
                "
              >
                {{ formatItemDiscount(item) }}
              </td>
              <td
                style="
                  border: 1px solid black;
                  padding: 8px;
                  text-align: right;
                  width: 12%;
                "
              >
                {{ formatItemFinalAmount(item) }}
              </td>
            </tr>

            <tr>
              <td
                colspan="3"
                style="padding: 10px; text-align: right; border: none"
              >
                <strong>Total</strong>
              </td>
              <td
                style="
                  padding: 10px;
                  text-align: right;
                  border: 1px solid black;
                "
              >
                {{ totalBeforeDiscount }}
              </td>
              <td
                style="
                  padding: 10px;
                  text-align: right;
                  border: 1px solid black;
                "
              >
                {{ totalBacklinkDiscount }}
              </td>
              <td
                style="
                  padding: 10px;
                  text-align: right;
                  border: 1px solid black;
                "
              >
                {{ parseFloat(subTotalAmount || 0).toFixed(2) }}
              </td>
            </tr>
            <tr v-if="parseFloat(discountAmount || 0) > 0">
              <td
                colspan="5"
                style="padding: 10px; text-align: right; border: none"
              >
                Additional Discount<template v-if="discountType === 'percentage'">
                  ({{ discount_value }}%)</template
                >
              </td>
              <td
                style="
                  padding: 10px;
                  text-align: right;
                  border: 1px solid black;
                "
              >
                {{ parseFloat(discountAmount || 0).toFixed(2) }}
              </td>
            </tr>
            <tr>
              <td
                colspan="5"
                style="padding: 10px; text-align: right; border: none"
              >
                Additional Fee<template v-if="feeType === 'percentage'">
                  ({{ fee_value }}%)</template
                >
              </td>
              <td
                style="
                  padding: 10px;
                  text-align: right;
                  border: 1px solid black;
                "
              >
                {{ parseFloat(feeAmount || 0).toFixed(2) }}
              </td>
            </tr>

            <tr>
              <td
                colspan="5"
                class="text-danger"
                style="padding: 10px; text-align: right; border: none"
              >
                Grand Total
              </td>
              <td
                style="
                  padding: 10px;
                  text-align: right;
                  border: 1px solid black;
                "
              >
                {{ parseFloat(grandTotalAmount || 0).toFixed(2) }}
              </td>
            </tr>
          </tbody>
        </table>

        <div style="margin-top: 30px">
          <h4 class="mb-1" style="text-transform: capitalize">
            In Words: {{ getCurrency() }}
            {{ numWords(Math.round(parseFloat(grandTotalAmount || 0))) }}
            <span v-if="parseFloat(grandTotalAmount || 0) % 1 !== 0"
              >approximately</span
            >
            Only
          </h4>

          <h4 v-if="remarks"><strong>Note/Terms</strong></h4>
          <p v-if="remarks">{{ remarks ?? "N/A" }}</p>
        </div>

        <h2 style="text-align: center; margin-top: 50px; margin-bottom: 50px">
          Thank You For Your Business
        </h2>
        <br />
      </div>
    </div>
  </section>
</template>

<style lang="scss">
.invoice-add-wrapper {
  .invoice-add {
    .invoice-preview-card {
      .invoice-date-wrapper {
        display: flex;
        align-items: center;
        width: 100%;

        .invoice-date-picker {
          flex: 1;
        }
      }
    }
  }
}

.log-scrollable {
  min-height: 200px;
  max-height: 500px;
  overflow-y: scroll;
}
</style>

<script>
import {
  BCard,
  BAvatar,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BDropdown,
  BDropdownItem,
  BButton,
  BForm,
  BModal,
  BSpinner,
  BRow,
  BCol,
  VBTooltip,
  BCardBody,
  BCardText,
  VBToggle,
  BLink,
  BInputGroup,
  BInputGroupPrepend,
  BFormTextarea,
  BTableLite,
  BFormCheckbox,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, max } from "@validations";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapGetters } from "vuex";
import UserAssignPolicyForm from "@/views/admin/payroll-management/UserAssignPolicyForm.vue";
import flatPickr from "vue-flatpickr-component";
import Logo from "@core/layouts/components/Logo.vue";
import { OUTREACH_INVOICE_ATTACHMENT_UPLOAD_AFTER_PAYMENT, OUTREACH_INVOICE_PAYMENT } from "@/helpers/permissionsConstant";
import UserCard from "@/layouts/components/UserCard.vue";
import {
  paymentPaidConstants,
  paymentUnpaidConstants,
  paymentDueConstants,
  outreachInvoiceStatusConstants,
} from "@/helpers/constant/outreachPaymentStatusConstant";
import numWords from "num-words";
import AppTimeline from "@core/components/app-timeline/AppTimeline.vue";
import AppTimelineItem from "@core/components/app-timeline/AppTimelineItem.vue";

export default {
  name: "OutreachInvoiceDetailsView",
  components: {
    BFormCheckbox,
    UserCard,
    UserAssignPolicyForm,
    BForm,
    BButton,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BSpinner,
    BRow,
    BCol,
    VBTooltip,
    BCardBody,
    BCardText,
    VBToggle,
    BLink,
    Logo,
    BInputGroup,
    BInputGroupPrepend,
    flatPickr,
    BFormTextarea,
    BTableLite,
    AppTimeline,
    AppTimelineItem,
  },
  directives: {
    "b-tooltip": VBTooltip,
    "b-toggle": VBToggle,

    Ripple,
  },
  data() {
    return {
      OUTREACH_INVOICE_PAYMENT,
      OUTREACH_INVOICE_ATTACHMENT_UPLOAD_AFTER_PAYMENT,


      paymentPaidConstants,
      paymentUnpaidConstants,
      paymentDueConstants,
      outreachInvoiceStatusConstants,

      showLogo: true,
      attachments: null,
      approver: null,
      clientAddress: "",

      invoiceItems: [],

      // data
      companyName: "",
      officeAddress: "",
      officeEmail: "",
      officeMobile: "",

      sellerName: "",
      sellerEmail: "",
      sellerContact: "",

      invoiceId: "",
      invoiceDate: "",
      status: "",
      remarks: "",
      paymentDate: "",

      targetedLink: "",
      costPrice: "",
      currency: "",

      subTotalAmount: 0,
      feeAmount: 0,
      discountType: "",
      discount_value: 0,

      feeType: "",
      fee_value: 0,
      discountAmount: 0,
      grandTotalAmount: 0,

      paymentMethod: "",
      paymentDetails: "",

      // Activity Log Data
      activityLogs: [],
      currentPage: 1,
      lastPage: false,
      loadingLog: false,

      // Reference Data
      filteredNicheOptions: [],
      filteredSellerOptions: [],
      filteredCurrencyOptions: [],
      filteredPaymentOptions: [
        { id: "paypal", name: "PayPal" },
        { id: "cash", name: "Cash" },
        { id: "mobile_banking", name: "Mobile banking" },
        { id: "crypto", name: "Crypto" },
        { id: "payoneer", name: "Payoneer" },
        { id: "bank", name: "Bank Transfer" },
      ],
      filteredApproverOptions: [],
    };
  },

  computed: {
    ...mapGetters({
      permissions: "userModule/getPermissions",
    }),
    statusVariant() {
      const statusColor = {
        true: "light-success",
        false: "light-danger",
      }

      return (status) => statusColor[status]
    },
    company() {
      return this.$store.getters["companyModule/data"]
    },
    totalBeforeDiscount() {
      return this.invoiceItems.reduce((sum, item) => sum + parseFloat(item.cost_price || 0), 0).toFixed(2)
    },
    totalBacklinkDiscount() {
      return this.invoiceItems.reduce((sum, item) => sum + parseFloat(item.discount_amount || 0), 0).toFixed(2)
    },
  },

  async created() {
    await this.$store.dispatch("companyModule/fetchCompanyData"),
    await Promise.all([
      this.getActivityLog(this.$route.params.id),
      this.getAllNiches(),
      this.getAllSellers(),
      this.getAllCurrencies(),
      this.getAllApprovers(),
    ]);
    await this.loadItems();
  },
  methods: {
    numWords,

    getCurrency() {
      return this.invoiceItems[0]?.currency?.data?.currency
    },

    formatItemDiscount(item) {
      const amount = parseFloat(item.discount_amount || 0)
      if (amount > 0) {
        if (item.discount_type === 'percentage' && item.discount_value) {
          return `${amount.toFixed(2)} (${item.discount_value}%)`
        }
        return amount.toFixed(2)
      }
      return '0.00'
    },

    formatItemFinalAmount(item) {
      const cost = parseFloat(item.cost_price || 0)
      const discount = parseFloat(item.discount_amount || 0)
      return (cost - discount).toFixed(2)
    },

    payInvoice() {
      this.$swal({
        title: "Warning!",
        text: "Are You Sure You Want To Pay This?",
        icon: "warning",
        html: `
            <div class="swal-content">
              <p>Please enter your password to confirm the payment.</p>
              <input type="password" id="swal-password" class="swal2-input form-control" placeholder="Enter your password">
              <p class="mt-2">If you have any file please attach below.</p>
              <input type="file" id="swal-files" class="swal2-file form-control my-1" multiple>
            </div>
    `,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: "Ok",
        showLoaderOnConfirm: true,
        preConfirm: () => {
          const password = document.getElementById("swal-password").value;
          const fileInput = document.getElementById("swal-files");
          const files = fileInput.files;

          if (!password) {
            this.$swal.showValidationMessage("Password is required.");
            return false;
          }

          return { password, files };
        },
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const id = this.$route.params.id;
            const formData = new FormData();
            formData.append("password", result.value.password);

            // Append multiple files
            for (let i = 0; i < result.value.files.length; i++) {
              formData.append("attachments[]", result.value.files[i]);
            }

            formData.append("_method", "PUT");

            // Send the form data with files and password
            await this.$api.post(`/api/outreach-invoices/${id}/pay`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });

            this.loadItems();

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: "Invoice Successfully Paid",
              },
            });
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Error",
                icon: "BellIcon",
                variant: "danger",
                text: error?.response?.data?.message || "An error occurred.",
              },
            });
          }
        }
      });
    },

    addAttachment() {
      this.$swal({
        title: "Add Attachment",
        text: "Please select files to attach to this invoice.",
        icon: "info",
        html: `
            <div class="swal-content text-left">
              <p class="mt-2">Select files to upload:</p>
              <input type="file" id="swal-attachment-files" class="swal2-file form-control my-1" multiple>
            </div>
        `,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: "Submit",
        showLoaderOnConfirm: true,
        preConfirm: () => {
          const fileInput = document.getElementById("swal-attachment-files");
          const files = fileInput.files;

          if (files.length === 0) {
            this.$swal.showValidationMessage("Please select at least one file.");
            return false;
          }

          return { files };
        },
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const id = this.$route.params.id;
            const formData = new FormData();

            for (let i = 0; i < result.value.files.length; i++) {
              formData.append("attachments[]", result.value.files[i]);
            }

            formData.append("_method", "PUT");

            // Just build UI and ok to send a post api call. Dont have to implement api right now i will update it later.
            await this.$api.post(`/api/outreach-invoices/${id}/add-attachment`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });

            this.loadItems();

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: "Attachment Successfully Added",
              },
            });
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Error",
                icon: "BellIcon",
                variant: "danger",
                text: error?.response?.data?.message || "An error occurred while uploading.",
              },
            });
          }
        }
      });
    },

    getBacklinkUrl(backlink) {
      return `${backlink?.site_list?.website}/${backlink.backlink_url}`
    },

    formatWebsite(website) {
      return website
    },

    formatDateTime(value) {
      if (value) {
        return this.$moment(value).format("MMM Do YYYY");
      }
    },

    formatDate(value) {
      if (value) {
        return this.$moment(value).format("MMM Do YYYY");
      }
      return "Not Set";
    },

    // Activity Log Methods
    handleScroll(event) {
      const scrollContainer = event.target;
      const bottomReached =
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
        scrollContainer.scrollHeight - 10;

      if (bottomReached && !this.loadingLog) {
        this.getActivityLog(this.$route.params.id);
      }
    },

    async getActivityLog(id) {
      if (this.loadingLog || this.lastPage) return;

      this.loadingLog = true;
      try {
        const response = await this.$api.get(
          `api/outreach/outreach-invoice/activity-log/${id}`,
          {
            params: {
              page: this.currentPage,
              per_page: 10,
            },
          }
        );

        const data = response.data.data || response.data;
        // console.log(data);
        if (Array.isArray(data)) {
          this.activityLogs.push(...data);
        } else if (
          data &&
          typeof data === "object" &&
          data.data &&
          Array.isArray(data.data)
        ) {
          this.activityLogs.push(...data.data);
        }

        this.currentPage++;

        const lastPage =
          response.data.last_page ||
          (response.data.meta ? response.data.meta.last_page : 1);

        if (this.currentPage > lastPage) {
          this.lastPage = true;
        }
      } catch (error) {
        console.error("Failed to fetch activity logs", error);
      } finally {
        this.loadingLog = false;
      }
    },

    formatLogTitle(log) {
      const attributes = log.properties
        ? log.properties.attributes ||
          (log.properties.changes ? log.properties.changes.new : log.properties)
        : {};

      const filteredAttributes = this.getFilteredAttributes(log);

      if (log.event === "updated") {
        if (filteredAttributes.status !== undefined) {
          const statusName = this.formatChangeValue(
            "status",
            filteredAttributes.status
          );
          if (statusName == "Invalid date") {
            return `<b>Invoice Paid by ${
              log.causer ? log.causer.name : "System"
            }</b>`;
          }
          // return `<b>Status</b> changed to <b>${statusName}</b> by <b>${
          //   log.causer ? log.causer.name : "System"
          // }</b>`;
        }
        if (
          filteredAttributes.review !== undefined &&
          filteredAttributes.review == 1
        ) {
          return `<b>Approved</b> by <b>${
            log.causer ? log.causer.name : "System"
          }</b>`;
        }
      }

      if (log.description === "created") {
        return `<b>Invoice</b> was <b>created</b> by <b>${
          log.causer ? log.causer.name : "System"
        }</b>`;
      }

      if (log.description === "attachment_added") {
        const files = log.properties?.files || [];
        const fileList = files.length > 0 ? ` (${files.join(", ")})` : "";
        return `<b>Attachment${
          files.length > 1 ? "s" : ""
        }</b> was <b>added</b>${fileList} by <b>${
          log.causer ? log.causer.name : "System"
        }</b>`;
      }

      return `<b>Invoice</b> was <b>${log.description}</b> by <b>${
        log.causer ? log.causer.name : "System"
      }</b>`;
    },

    shouldShowChangesSection(log) {
      return (
        log.description !== "created" &&
        log.properties &&
        log.properties.attributes &&
        Object.keys(this.getFilteredAttributes(log)).length > 0
      );
    },

    hasDisplayableChanges(log) {
      const attributes = this.getFilteredAttributes(log);
      return Object.keys(attributes).some((key) =>
        this.shouldShowAttribute(log, key, attributes[key])
      );
    },

    shouldShowAttribute(log, key, value) {
      if (value === null || value === undefined) return false;

      const formattedValue = this.formatChangeValue(key, value);
      if (formattedValue === "N/A") return false;

      // Hide review and status if they are already in the title
      if (log.event === "updated") {
        const filteredAttributes = this.getFilteredAttributes(log);

        if (key === "status" && filteredAttributes.status !== undefined) {
          return false;
        }

        if (
          key === "review" &&
          filteredAttributes.review !== undefined &&
          filteredAttributes.review == 1
        ) {
          return false;
        }
      }

      if (formattedValue === "Pending Review") return false;
      if (formattedValue === "Reviewed/Approved") return false;

      return true;
    },

    copyToClipboard(text) {
      const textToCopy = text;

      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(textToCopy).then(
          () => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Copied",
                icon: "CheckCircleIcon",
                variant: "success",
                text: "Invoice ID copied to clipboard!",
              },
            });
          },
          (err) => {
            // If failed, try fallback
            this.fallbackCopyTextToClipboard(textToCopy);
          }
        );
      } else {
        // Fallback for non-secure contexts or older browsers
        this.fallbackCopyTextToClipboard(textToCopy);
      }
    },

    fallbackCopyTextToClipboard(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;

      // Ensure it's not visible but part of the DOM
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand("copy");
        const msg = successful ? "successful" : "unsuccessful";
        if (successful) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Copied",
              icon: "CheckCircleIcon",
              variant: "success",
              text: "URL copied to clipboard!",
            },
          });
        } else {
          throw new Error("Copy command failed");
        }
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Failed",
            icon: "AlertCircleIcon",
            variant: "danger",
            text: "Failed to copy URL!",
          },
        });
      }

      document.body.removeChild(textArea);
    },

    formatLogDateTime(value) {
      if (value) {
        return this.$moment(value).format("MMM Do YYYY, h:mm:ss a");
      }
    },

    formatChangeKey(key) {
      if (key === "id") return "ID";

      const keyMap = {
        nich_id: "Niche",
        seller_id: "Seller",
        currency_id: "Currency",
        company_id: "Company",
        payment_method: "Payment Method",
        approver_id: "Approver",
        total_amount: "Total Amount",
        fee_amount: "Fee Amount",
        discount_amount: "Discount Amount",
        grand_total: "Grand Total",
        invoice_number: "Invoice Number",
        created_at: "Created At",
        updated_at: "Updated At",
      };

      if (keyMap[key]) return keyMap[key];

      return key
        .replace(/_id$/, "")
        .split("_")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    },

    formatChangeValue(key, value) {
      // console.log(key, value);
      if (value === null || value === undefined) return "N/A";

      if (key === "nich_id") {
        const niche = (this.filteredNicheOptions || []).find(
          (n) => n.id === value
        );
        return niche ? niche.name : value;
      }
      if (key === "seller_id") {
        const seller = (this.filteredSellerOptions || []).find(
          (s) => s.id === value
        );
        return seller ? seller.name : value;
      }
      if (key === "currency_id") {
        const currency = (this.filteredCurrencyOptions || []).find(
          (c) => c.id === value
        );
        return currency ? currency.currency || currency.name : value;
      }
      if (key === "payment_method") {
        const payment = (this.filteredPaymentOptions || []).find(
          (p) => p.id === value
        );
        return payment ? payment.name : value;
      }
      if (key === "approver_id") {
        const approver = (this.filteredApproverOptions || []).find(
          (a) => a.id === value
        );
        return approver ? approver.name : value;
      }
      if (key === "company_id") {
        return this.company?.id === value ? this.company.name : value;
      }
      if (key.includes("date") || key.includes("at")) {
        return value ? this.$moment(value).format("MMM Do YYYY") : "N/A";
      }
      if (
        key.includes("amount") ||
        key.includes("price") ||
        key.includes("fee")
      ) {
        return parseFloat(value).toFixed(2);
      }
      if (key.includes("value")) {
        // Simple number formatting
        return value;
      }
      if (key === "status") {
        const status = (outreachInvoiceStatusConstants || []).find(
          (s) => s.value === value
        );
        return status ? status.name : value;
      }
      if (key === "review") {
        return value ? "Reviewed/Approved" : "Pending Review";
      }

      return value;
    },

    getFilteredAttributes(log) {
      if (!log.properties || !log.properties.attributes) return {};

      if (log.event !== "updated") {
        const attributes = { ...log.properties.attributes };
        const ignoredKeys = ["updated_at", "created_at", "id"];
        ignoredKeys.forEach((key) => delete attributes[key]);
        // Also remove nulls
        Object.keys(attributes).forEach((key) => {
          if (attributes[key] === null || attributes[key] === undefined) {
            delete attributes[key];
          }
        });
        return attributes;
      }

      const filtered = {};
      const attributes = log.properties.attributes;
      const old = log.properties.old || {};
      const ignoredKeys = ["updated_at", "created_at", "id"];

      Object.keys(attributes).forEach((key) => {
        if (
          !ignoredKeys.includes(key) &&
          attributes[key] !== null &&
          attributes[key] !== undefined &&
          JSON.stringify(old[key]) !== JSON.stringify(attributes[key])
        ) {
          filtered[key] = attributes[key];
        }
      });

      return filtered;
    },

    async getAllSellers() {
      try {
        const response = await this.$api.get("api/outreach/seller/all");
        this.filteredSellerOptions = response?.data?.data || [];
      } catch (error) {
        console.error(error);
      }
    },

    async getAllNiches() {
      try {
        const response = await this.$api.get("api/outreach/niche/all");
        this.filteredNicheOptions = response?.data?.data || [];
      } catch (error) {
        console.error(error);
      }
    },

    async getAllCurrencies() {
      try {
        const response = await this.$api.get("api/currencies/all");
        this.filteredCurrencyOptions = response?.data?.data || [];
      } catch (error) {
        console.error(error);
      }
    },

    async printInvoice() {
      const printSection = document.getElementById("content");
      // const originalContent = document.body.innerHTML;

      // Create a new window for printing
      const newWindow = window.open("", "_blank", "width=600,height=400");
      newWindow.document.write(`
        <html>
          <head>
            <title>Print</title>
            <style>
              @page {
                  size: auto;
                  margin: 20px;
                }

              /* Add any styles you want for the printed content here */
              body {
                font-family: Arial, sans-serif;
                margin: 20px;
              }
              /* Example: If you want page breaks to happen automatically */
              .page-break { page-break-before: always; }

              /* Ensure the print layout is clean and well-organized */
              @media print {
                body {
                  margin: 0;
                  padding: 0;
                  font-size: 14px;
                }
                .no-print {
                  display: none;
                }
                /* Custom styling for your content */
              }
            </style>
          </head>
          <body>
            ${printSection.innerHTML}
          </body>
        </html>
      `);
      newWindow.document.close(); // Close the document
      newWindow.print(); // Trigger the print dialog
      newWindow.addEventListener("afterprint", function () {
        newWindow.close();
      });
    },
    async getOutreachInvoiceItems() {
      const invoiceId = this.$route.params.id;
      return await this.$api.get(
        `api/outreach-invoices/${invoiceId}?include=site_list,site_list.seller,backlinks,backlinks.project, backlinks.currency ,approver`
      );
    },
    async getSetting(params) {
      return await this.$api.get("/api/settings", {
        params: {
          select: params.select,
        },
      });
    },

    async loadItems() {
      try {
        const [getSetting, outreachInvoiceDetails] = await Promise.all([
          this.getSetting({
            select: "account_id",
          }),
          this.getOutreachInvoiceItems(),
        ]);

        const data = outreachInvoiceDetails?.data?.data;

        this.attachments = data?.attachments;

        this.sellerName = data?.seller?.name;
        this.sellerEmail = data?.seller?.email;
        this.sellerContact = data?.seller?.contact_no;

        this.status = data?.status;
        this.remarks = data?.remarks;
        this.invoiceId = data?.invoice_number;
        this.invoiceDate = this.formatDateTime(data?.created_at);
        this.paymentDate = this.formatDateTime(data?.payment_date);
        this.approver = data?.approver?.data;

        this.paymentMethod = data?.payment_method_text;
        this.paymentDetails = data?.payment_details;

        this.invoiceItems = data?.backlinks?.data;

        this.subTotalAmount = data?.total_amount;
        this.feeAmount = data?.fee_amount;
        this.discountType = data?.discount_type;
        this.discount_value = data?.discount_value;

        this.feeType = data?.fee_type;
        this.fee_value = data?.fee_value;

        this.discountAmount = data?.discount_amount;
        this.grandTotalAmount = data?.grand_total;

        this.feeType = data?.fee_type;
        this.fee_value = data?.fee_value;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Warning",
            icon: "BellIcon",
            variant: "warning",
            text: error?.response?.data?.message,
          },
        });
      }
    },
    async getAllNiches() {
      try {
        const response = await this.$api.get("api/outreach/niche/all");
        this.filteredNicheOptions = response?.data?.data || [];
      } catch (error) {
        console.error(error);
      }
    },

    async getAllSellers() {
      try {
        const response = await this.$api.get("api/outreach/seller/all");
        this.filteredSellerOptions = response?.data?.data || [];
      } catch (error) {
        console.error(error);
      }
    },

    async getAllCurrencies() {
      try {
        const response = await this.$api.get("api/currencies/all");
        this.filteredCurrencyOptions = response?.data?.data || [];
      } catch (error) {
        console.error(error);
      }
    },

    async getAllApprovers() {
      try {
        const response = await this.$api.get("api/outreach-invoices/reviewers");
        this.filteredApproverOptions = response?.data?.data || [];
      } catch (error) {
        console.error(error);
      }
    },

  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "~@core/scss/base/pages/app-invoice.scss";

.form-item-section {
  background-color: $product-details-bg;
}

.form-item-action-col {
  width: 27px;
}

.repeater-form {
  // overflow: hidden;
  transition: 0.35s height;
}

.v-select {
  &.item-selector-title,
  &.payment-selector {
    background-color: #fff;

    .dark-layout & {
      background-color: unset;
    }
  }
}

.dark-layout {
  .form-item-section {
    background-color: $theme-dark-body-bg;

    .row .border {
      background-color: $theme-dark-card-bg;
    }
  }
}

.custom-width {
  max-width: 25rem !important;
}
.force-full-width {
  width: 100% !important;
}
</style>
